import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line import/extensions
import '@copilotkit/react-ui/styles.css';
import {
  Flex,
  IconButton,
  Spinner,
  Text,
  spaces,
  getToken,
  ColorsV2,
} from '@electricjs/arc';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { QuickPrompts } from './QuickPrompts';

import { CHAT_NAMESPACES } from '@/types/aiChat';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import { useCopilotKitAgentContext } from '@/components/CopilotKitProvider/CopilotKitAgentContext';
import { useStabilized } from '@/hooks/useStabilized';
import { useCopilotKitQuickPrompts } from '@/hooks/aiChat/useCopilotKitQuickPrompts';
import { copilotKitColorOverrides } from '@/pages/AIChatCopilotKit/CopilotKitStyles';
import { CopilotKitInitErrorBanner } from '@/pages/AIChatCopilotKit/CopilotKitInitErrorBanner';
import { MessagesList } from '@/pages/AIChatCopilotKit/MessagesList';
import { CustomChatInput } from '@/pages/AIChatCopilotKit/CustomChatInput';
import { FeedbackText } from '@/pages/AIChatCopilotKit/FeedbackText';
import { NewChatButton } from './NewChatButton';
import { ROUTES } from '@/constants/routes';

const DrawerHeader = styled(Flex)`
  flex-shrink: 0;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spaces.space_m_rem};
`;

const DrawerContent = styled(Flex)`
  height: 100%;
  flex: 1;
  overflow: hidden;
  ${copilotKitColorOverrides}
`;

const MessagesScrollContainer = styled(Flex)`
  flex: 1;
  overflow: hidden;
  width: 100%;
`;

const InputContainer = styled(Flex)`
  flex-shrink: 0;
  width: 100%;
  padding-top: ${spaces.space_m_rem};
  border-top: 1px solid ${getToken(ColorsV2.BORDER)};
`;

const ButtonGroup = styled(Flex)`
  gap: ${spaces.space_xs_rem};
  align-items: center;
`;

type CopilotKitAIChatDrawerContentProps = {
  onClose: () => void;
  /** When false, do not render suggestions (avoids duplicate list when full page also shows suggestions). */
  visible?: boolean;
};

/**
 * Inner content for the CopilotKit AI Chat Drawer.
 *
 * Renders the header, messages list, and chat input — no Drawer wrapper.
 * The Drawer shell is owned by CopilotKitAIChatDrawer, which keeps a single
 * Drawer mounted at all times (avoiding open/close/open animation jank when
 * CopilotKitProvider transitions from its loading state to ready).
 *
 * Must be rendered inside a CopilotKit context (calls CopilotKit hooks).
 */
export const CopilotKitAIChatDrawerContent = ({
  onClose,
  visible: isDrawerVisible = true,
}: CopilotKitAIChatDrawerContentProps) => {
  const { t } = useTranslation('ai-chat');
  const messagesListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Setup-assistant-only orgs have no /ai-chat route registered (see routes.tsx) —
  // hide the affordance rather than let it 404. Full general-chat access stays
  // hasAnyGigawattAccess-only, same as side nav and support center.
  const { hasAnyGigawattAccess } = useAIChatFeatureFlags();

  // Consume shared CopilotKit agent state (tools are registered once in CopilotKitAgentProvider)
  const {
    messages,
    sendMessage,
    isLoading,
    isAvailable,
    isCreatingNewConversation,
    needsHistoryRestore,
    isInitializing,
    isError,
    isRetrying,
    retryCreateConversation,
    pendingToolApproval,
    hasPendingFrontendToolApproval,
    runError,
  } = useCopilotKitAgentContext();

  // UI Stabilization: Prevent flickering during rapid CopilotKit state changes
  const stableIsLoading = useStabilized(isLoading);
  // stableIsInitializing: agent-level readiness (isAvailable/isCreatingNewConversation) — distinct
  // from context isInitializing, which gates the entire render until a threadId exists.
  const stableIsInitializing = useStabilized(
    !isAvailable || isCreatingNewConversation || needsHistoryRestore
  );

  const { quickPrompts, showQuickPrompts } = useCopilotKitQuickPrompts({
    isVisible: isDrawerVisible,
  });

  const handleOpenFullPage = () => {
    navigate(ROUTES.AI_CHAT);
    onClose();
  };

  // Initializing: render a minimal loading state within the drawer; the rest of the app is unaffected.
  if (isInitializing) {
    return (
      <Flex vertical height="100%">
        <DrawerHeader>
          <Text variant="heading-3">{t('drawer.title')}</Text>
          <ButtonGroup>
            <IconButton
              id="copilotkit-ai-chat-drawer-close-button"
              icon="close"
              onClick={onClose}
              intent="neutral"
              aria-label={t('drawer.closeChat')}
              testId="copilotkit-ai-chat-drawer-close-button"
            />
          </ButtonGroup>
        </DrawerHeader>
        <Flex justifyContent="center" padding="2rem">
          <Spinner />
        </Flex>
      </Flex>
    );
  }

  // Render a minimal error state — header remains visible so the user can close
  // the drawer or navigate away. The rest of the app is unaffected.
  if (isError && !isRetrying) {
    return (
      <Flex vertical height="100%">
        <DrawerHeader>
          <Text variant="heading-3">{t('drawer.title')}</Text>
          <ButtonGroup>
            <IconButton
              id="copilotkit-ai-chat-drawer-close-button"
              icon="close"
              onClick={onClose}
              intent="neutral"
              aria-label={t('drawer.closeChat')}
              testId="copilotkit-ai-chat-drawer-close-button"
            />
          </ButtonGroup>
        </DrawerHeader>
        <CopilotKitInitErrorBanner
          id="copilotkit-drawer-init-error-banner"
          onRetry={retryCreateConversation}
        />
      </Flex>
    );
  }

  return (
    <Flex vertical height="100%">
      <DrawerHeader>
        <Text variant="heading-3">{t('drawer.title')}</Text>
        <ButtonGroup>
          <NewChatButton
            id="new-chat-copilotkit-drawer-button"
            variant="outline"
            namespaceKey={CHAT_NAMESPACES.GIGAWATT_COPILOTKIT}
            isLoading={stableIsLoading}
          />
          {hasAnyGigawattAccess && (
            <IconButton
              id="copilotkit-ai-chat-drawer-open-full-page-button"
              icon="arrow-up-right-from-square"
              onClick={handleOpenFullPage}
              intent="neutral"
              aria-label={t('drawer.openFullPage')}
              testId="copilotkit-ai-chat-drawer-open-full-page-button"
            />
          )}
          <IconButton
            id="copilotkit-ai-chat-drawer-close-button"
            icon="close"
            onClick={onClose}
            intent="neutral"
            aria-label={t('drawer.closeChat')}
            testId="copilotkit-ai-chat-drawer-close-button"
          />
        </ButtonGroup>
      </DrawerHeader>

      <DrawerContent vertical>
        <MessagesScrollContainer vertical>
          <MessagesList
            ref={messagesListRef}
            messages={messages}
            isLoading={stableIsLoading}
            isInitializing={stableIsInitializing}
            runError={runError}
          />
          {showQuickPrompts && (
            <Flex px={spaces.space_m_rem} pb={spaces.space_s_rem}>
              <QuickPrompts
                prompts={quickPrompts}
                onSelect={async (messageText: string) => {
                  await sendMessage({
                    id: `msg-${Date.now()}-${Math.random()}`,
                    role: 'user',
                    content: messageText,
                  });
                }}
                disabled={stableIsLoading || isRetrying}
              />
            </Flex>
          )}
        </MessagesScrollContainer>

        <InputContainer vertical>
          <CustomChatInput
            inputId="copilot-chat-drawer-input"
            inProgress={stableIsLoading || isRetrying}
            disabled={!!pendingToolApproval || hasPendingFrontendToolApproval}
            onSend={async (messageText: string) => {
              await sendMessage({
                id: `msg-${Date.now()}-${Math.random()}`,
                role: 'user',
                content: messageText,
              });
            }}
          />
          <FeedbackText />
        </InputContainer>
      </DrawerContent>
    </Flex>
  );
};
